import { useState, useCallback } from 'react';
import { Identifiable } from '@sr/dto';
import { DialogProps } from 'shared/ui/form-dialog';
import { FormTools } from 'utils/form-tools';
import { FormikValues } from 'formik';
import { useSnack, NotificationsConfig } from 'shared/ui/snack/snack.hook';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';

export type DialogConfig<
  TItem extends Identifiable,
  TCreateItem,
  TUpdateItem,
  TFormData extends FormikValues
> = {
  addHandler: (createdDto: TCreateItem) => Promise<void>;
  updateHandler: (updateDto: TUpdateItem) => Promise<void>;
  formTools: FormTools<TItem, TCreateItem, TUpdateItem, TFormData>;
  notificationsConfig: NotificationsConfig;
};

type DialogState<TItem extends Identifiable, TFormData extends FormikValues> = 
  | { mode: 'closed' }
  | { mode: 'creating'; pendingData?: TFormData }
  | { mode: 'editing'; item: TItem; pendingData?: TFormData };

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  
  if (typeof error === 'string') {
    return new Error(error);
  }
  
  return new Error('Произошла неизвестная ошибка');
};

export function useCreateOrEditDialog<
  TItem extends Identifiable,
  TCreateItem,
  TUpdateItem,
  TFormData extends FormikValues
>({
  formTools,
  addHandler,
  updateHandler,
  notificationsConfig,
}: DialogConfig<TItem, TCreateItem, TUpdateItem, TFormData>) {
  const { showSuccessAdd, showFailedAdd, showSuccessUpdate, showFailedUpdate } = 
    useSnack(notificationsConfig);

  const [dialogState, setDialogState] = useState<DialogState<TItem, TFormData>>({ 
    mode: 'closed' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (data: TFormData) => {
    setIsSubmitting(true);

    try {
      if (dialogState.mode === 'editing') {
        await updateHandler(formTools.toUpdateDto(dialogState.item.id, data));
        showSuccessUpdate();
      } else {
        await addHandler(formTools.toCreateDto(data));
        showSuccessAdd();
      }
      
      setDialogState({ mode: 'closed' });
    } catch (error) {
      const normalizedError = normalizeError(extractNestJsErrorMessage(error));
      
      if (dialogState.mode === 'editing') {
        setDialogState({ 
          mode: 'editing', 
          item: dialogState.item, 
          pendingData: data 
        });
        showFailedUpdate(normalizedError);
      } else {
        setDialogState({ 
          mode: 'creating', 
          pendingData: data 
        });
        showFailedAdd(normalizedError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    setDialogState({ mode: 'closed' });
  }, []);

  const handleClosed = useCallback(() => {
  }, []);

  const openForAdd = useCallback(() => {
    setDialogState({ mode: 'creating' });
  }, []);

  const openForEdit = useCallback((item: TItem) => {
    setDialogState({ mode: 'editing', item });
  }, []);

  const getInitialValues = useCallback((): TFormData => {
    if (dialogState.mode === 'closed') {
      return formTools.emptyValues();
    }

    if (dialogState.pendingData) {
      return dialogState.pendingData;
    }

    if (dialogState.mode === 'editing') {
      return formTools.fromDto(dialogState.item);
    }

    return formTools.emptyValues();
  }, [dialogState, formTools]);

  const dialogProps: Omit<DialogProps<TFormData>, 'renderForm' | 'renderTitle'> & { 
    isSubmitting?: boolean 
  } = {
    open: dialogState.mode !== 'closed',
    onClose: handleClose,
    onClosed: handleClosed,
    validate: formTools.validate,
    onSubmit: handleSubmit,
    initialValues: getInitialValues(),
    isSubmitting,
  };

  return {
    openToAdd: openForAdd,
    openToEdit: openForEdit,
    itemToEdit: dialogState.mode === 'editing' ? dialogState.item : null,
    isCreating: dialogState.mode === 'creating',
    isEditing: dialogState.mode === 'editing',
    dialogProps,
  };
}